import { getAuditType } from "../audit/get-audit-type";
import { moment } from "../date";
import type {
	ProjectTimelineAssignment,
	ProjectTimelineItem,
	ProjectTimelineNoteWithFiles,
	Tables,
} from "../types";

export type ProjectTimeline = ReturnType<typeof createProjectTimeline>;

/**
 * This function creates a project timeline from the assignments and audit timelines.
 * @param param0
 * @returns
 */
export function createProjectTimeline({
	assignmentsTimeline,
	auditTimeline,
	projectNotesTimeline,
}: {
	assignmentsTimeline: ProjectTimelineAssignment[];
	auditTimeline: Tables<"projectsAudit">[];
	projectNotesTimeline: ProjectTimelineNoteWithFiles[];
}) {
	let timeline: Array<{
		date: string;
		items: Array<ProjectTimelineItem>;
	}> = [];

	for (const assignment of assignmentsTimeline) {
		const date = assignment.crew.plan.date;
		const dateIndex = timeline.findIndex((d) => d.date === date);
		if (dateIndex === -1) {
			timeline.push({
				date,
				items: [
					{
						type: "assignment",
						item: assignment,
					},
				],
			});
		} else {
			timeline[dateIndex]?.items.push({
				type: "assignment",
				item: assignment,
			});
		}
	}

	for (const note of projectNotesTimeline) {
		const date = note.date;
		const dateIndex = timeline.findIndex((d) => d.date === date);
		if (dateIndex === -1) {
			timeline.push({
				date,
				items: [
					{
						type: "note",
						item: note,
					},
				],
			});
		} else {
			timeline[dateIndex]?.items.push({
				type: "note",
				item: note,
			});
		}
	}

	for (const audit of auditTimeline) {
		const date = moment(audit.changedAt).format("YYYY-MM-DD");
		const dateIndex = timeline.findIndex((d) => d.date === date);
		const type = getAuditType(audit.operation);
		if (dateIndex === -1) {
			timeline.push({
				date,
				items: [
					{
						type,
						item: audit,
					},
				],
			});
		} else {
			timeline[dateIndex]?.items.push({
				type,
				item: audit,
			});
		}
	}

	timeline.sort((a, b) => {
		if (a.date > b.date) return -1;
		if (a.date < b.date) return 1;
		return 0;
	});

	timeline = timeline.map((dateItem) => {
		dateItem.items.sort((a, b) => {
			if (a.type === "audit-delete") return 1; // Ensure deletes are at the end
			if (a.type === "audit-insert") return 1; // Ensure inserts are at the start
			if (a.type === "audit-update") return 1; // Ensure updates are in the middle
			if (a.type === "assignment") {
				if (b.type === "assignment") {
					return 3;
				} else {
					return -1; // Assignments should come before notes and audits
				}
			}
			if (a.type === "note") {
				if (b.type === "note") {
					return ((b.item as ProjectTimelineNoteWithFiles).createdAt ?? "").localeCompare(
						(a.item as ProjectTimelineNoteWithFiles).createdAt ?? "",
					);
				} else {
					return 1; // Notes should come after assignments and before audits
				}
			}
			return 0;
		});

		return dateItem;
	});

	return timeline;
}
